import React, { useEffect, useState } from "react";
import { useOccasionTheme } from "@/hooks/useOccasionTheme";

interface FloatingItem {
  id: number;
  type: "crescent" | "greeting";
  left: number;
  delay: number;
  duration: number;
  size: number;
}

const RamadanDecorations: React.FC = () => {
  const { activeTheme } = useOccasionTheme();
  const [floatingItems, setFloatingItems] = useState<FloatingItem[]>([]);

  // Only render if Ramadan theme is active
  const isRamadan = activeTheme?.slug === "ramadan";

  useEffect(() => {
    if (!isRamadan) return;

    // Initial burst on landing - crescents + greeting
    const initialItems: FloatingItem[] = [
      ...Array.from({ length: 5 }, (_, i) => ({
        id: Date.now() + i,
        type: "crescent" as const,
        left: 10 + i * 18,
        delay: i * 0.3,
        duration: 7 + Math.random() * 2,
        size: 18 + Math.random() * 8,
      })),
      {
        id: Date.now() + 100,
        type: "greeting" as const,
        left: 40,
        delay: 0.5,
        duration: 9,
        size: 24,
      },
    ];
    setFloatingItems(initialItems);

    // Clear initial items after they float away
    const clearInitial = setTimeout(() => {
      setFloatingItems([]);
    }, 12000);

    // After landing, send one crescent every 30 seconds
    const interval = setInterval(() => {
      const newItem: FloatingItem = {
        id: Date.now(),
        type: "crescent",
        left: 10 + Math.random() * 80,
        delay: 0,
        duration: 8,
        size: 18 + Math.random() * 10,
      };
      setFloatingItems((prev) => [...prev, newItem]);

      // Remove after animation completes
      setTimeout(() => {
        setFloatingItems((prev) => prev.filter((item) => item.id !== newItem.id));
      }, 9000);
    }, 30000);

    return () => {
      clearTimeout(clearInitial);
      clearInterval(interval);
    };
  }, [isRamadan]);

  if (!isRamadan) {
    return null;
  }

  const renderItem = (item: FloatingItem) => {
    switch (item.type) {
      case "crescent":
        return <span className="text-yellow-400/80">🌙</span>;
      case "greeting":
        return (
          <span className="bg-gradient-to-r from-blue-700 to-indigo-800 text-yellow-300 px-3 py-1 rounded-full text-sm font-medium shadow-lg whitespace-nowrap border border-yellow-400/30">
            🌙 Ramadan Kareem 🌙
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <>
      {/* Floating Items */}
      <div className="fixed inset-0 pointer-events-none z-40" style={{ overflow: 'visible' }}>
        {floatingItems.map((item) => (
          <div
            key={item.id}
            className="absolute top-0 animate-float-ramadan"
            style={{
              left: `${item.left}%`,
              animationDelay: `${item.delay}s`,
              animationDuration: `${item.duration}s`,
              fontSize: `${item.size}px`,
            }}
          >
            {renderItem(item)}
          </div>
        ))}
      </div>

      {/* Subtle blue/gold overlay gradient at edges */}
      <div className="fixed inset-0 pointer-events-none z-30">
        <div className="absolute top-0 left-0 right-0 h-16 bg-gradient-to-b from-indigo-900/10 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-amber-500/5 to-transparent" />
      </div>
    </>
  );
};

export default RamadanDecorations;
